import React from "react";
import { Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { SpotRequest, MergeSpotRequest, SpotAddRequest, getMergeRequestDistance, hasMergeRequestValidCoordinates, isValidCoordinate } from "@/common/model/SpotRequest";

interface SpotInfo {
  id: number;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
}

interface RequestMapMarkersProps {
  requests: SpotRequest[];
  selectedRequest: SpotRequest | null;
  hoveredRequest: SpotRequest | null;
  onRequestSelect: (request: SpotRequest) => void;
  onReview: (request: SpotRequest) => void;
  onSwap?: (request: SpotRequest) => void;
  onShowSpotStats?: (spot: SpotInfo) => void;
}

// Create marker icon for merge source spot (red)
const createSourceMarkerIcon = (isSelected: boolean, isHovered: boolean) => {
  const size = isSelected ? 32 : isHovered ? 28 : 24;
  const color = "#ef4444"; // red-500

  return L.divIcon({
    className: "merge-source-marker",
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        ${isSelected || isHovered ? 'animation: pulse 1.5s infinite;' : ''}
      ">
        <svg width="${size * 0.5}" height="${size * 0.5}" viewBox="0 0 24 24" fill="white">
          <path d="M19 13H5v-2h14v2z"/>
        </svg>
      </div>
      <style>
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      </style>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Create marker icon for merge target spot (green)
const createTargetMarkerIcon = (isSelected: boolean, isHovered: boolean) => {
  const size = isSelected ? 32 : isHovered ? 28 : 24;
  const color = "#22c55e"; // green-500

  return L.divIcon({
    className: "merge-target-marker",
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        ${isSelected || isHovered ? 'animation: pulse 1.5s infinite;' : ''}
      ">
        <svg width="${size * 0.5}" height="${size * 0.5}" viewBox="0 0 24 24" fill="white">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Create marker icon for add request (green plus)
const createAddMarkerIcon = (isSelected: boolean, isHovered: boolean) => {
  const size = isSelected ? 36 : isHovered ? 32 : 28;
  const color = "#22c55e"; // green-500

  return L.divIcon({
    className: "add-request-marker",
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        ${isSelected || isHovered ? 'animation: pulse 1.5s infinite;' : ''}
      ">
        <svg width="${size * 0.5}" height="${size * 0.5}" viewBox="0 0 24 24" fill="white">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
        </svg>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Create marker icon for rename request (blue pencil)
const createRenameMarkerIcon = (isSelected: boolean, isHovered: boolean) => {
  const size = isSelected ? 36 : isHovered ? 32 : 28;
  const color = "#3b82f6"; // blue-500

  return L.divIcon({
    className: "rename-request-marker",
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        ${isSelected || isHovered ? 'animation: pulse 1.5s infinite;' : ''}
      ">
        <svg width="${size * 0.5}" height="${size * 0.5}" viewBox="0 0 24 24" fill="white">
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
        </svg>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

const RequestMapMarkers: React.FC<RequestMapMarkersProps> = ({
  requests,
  selectedRequest,
  hoveredRequest,
  onRequestSelect,
  onReview,
  onSwap,
  onShowSpotStats,
}) => {
  const isRequestSelected = (request: SpotRequest) =>
    selectedRequest?.id === request.id && selectedRequest?.type === request.type;

  const isRequestHovered = (request: SpotRequest) =>
    hoveredRequest?.id === request.id && hoveredRequest?.type === request.type;

  const renderMergeMarkers = (request: SpotRequest) => {
    const data = request.data as MergeSpotRequest;

    // Skip if coordinates are invalid
    if (!hasMergeRequestValidCoordinates(data)) {
      console.warn(`Merge request ${request.id} has invalid coordinates, skipping map markers`);
      return null;
    }

    const isSelected = isRequestSelected(request);
    const isHovered = isRequestHovered(request);
    const distance = getMergeRequestDistance(data);

    return (
      <React.Fragment key={`merge-${request.id}`}>
        {/* Connection line */}
        <Polyline
          positions={[
            [data.sourceLat!, data.sourceLon!],
            [data.targetLat!, data.targetLon!],
          ]}
          pathOptions={{
            color: isSelected ? "#3b82f6" : isHovered ? "#60a5fa" : "#6b7280",
            weight: isSelected ? 3 : 2,
            dashArray: isSelected ? undefined : "5, 5",
            opacity: isSelected || isHovered ? 1 : 0.6,
          }}
        />

        {/* Source marker (red - will be deleted) */}
        <Marker
          position={[data.sourceLat!, data.sourceLon!]}
          icon={createSourceMarkerIcon(isSelected, isHovered)}
          eventHandlers={{
            click: () => onRequestSelect(request),
          }}
        >
          <Popup className="merge-popup" maxWidth={320}>
            <div className="p-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="font-bold text-red-600">SOURCE (will be deleted)</span>
              </div>
              <h3 className="font-semibold text-lg text-gray-900">{data.sourcespotname}</h3>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-100 rounded p-2">
                  <div className="text-gray-500 text-xs">Sessions</div>
                  <div className="font-semibold text-gray-900">{data.sourcensessions ?? 0}</div>
                </div>
                <div className="bg-gray-100 rounded p-2">
                  <div className="text-gray-500 text-xs">Reviews</div>
                  <div className="font-semibold text-gray-900">{data.sourcenreviews ?? 0}</div>
                </div>
              </div>
              <div className="mt-3 text-sm text-gray-500">
                <span className="font-medium">{distance.toFixed(2)} km</span> from target
              </div>
            </div>
          </Popup>
        </Marker>

        {/* Target marker (green - will be kept) */}
        <Marker
          position={[data.targetLat!, data.targetLon!]}
          icon={createTargetMarkerIcon(isSelected, isHovered)}
          eventHandlers={{
            click: () => onRequestSelect(request),
          }}
        >
          <Popup className="merge-popup" maxWidth={400}>
            <div className="p-3">
              {/* Header */}
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                <span className="font-bold text-gray-900">MERGE REQUEST</span>
              </div>

              {/* Two spot comparison */}
              <div className="grid grid-cols-2 gap-3">
                {/* Source */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="text-xs font-medium text-red-600">SOURCE</span>
                    </div>
                    {onShowSpotStats && (data.selectedspotid || data.sourcespotid) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onShowSpotStats({
                            id: data.selectedspotid ?? data.sourcespotid!,
                            name: data.sourcespotname,
                            latitude: data.sourceLat,
                            longitude: data.sourceLon,
                          });
                        }}
                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                        title="View spot statistics"
                      >
                        Stats
                      </button>
                    )}
                  </div>
                  <div className="font-semibold text-sm text-gray-900 truncate">{data.sourcespotname}</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {data.sourcensessions ?? 0} sessions · {data.sourcenreviews ?? 0} reviews
                  </div>
                </div>

                {/* Target */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-xs font-medium text-green-600">TARGET</span>
                    </div>
                    {onShowSpotStats && data.targetspotid && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onShowSpotStats({
                            id: data.targetspotid,
                            name: data.targetspotname,
                            latitude: data.targetLat,
                            longitude: data.targetLon,
                          });
                        }}
                        className="text-xs text-green-600 hover:text-green-800 font-medium"
                        title="View spot statistics"
                      >
                        Stats
                      </button>
                    )}
                  </div>
                  <div className="font-semibold text-sm text-gray-900 truncate">{data.targetspotname}</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {data.targetnsessions ?? 0} sessions · {data.targetnreviews ?? 0} reviews
                  </div>
                </div>
              </div>

              {/* Distance and user */}
              <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
                <span>Distance: <span className="font-medium">{distance.toFixed(2)} km</span></span>
                <span>By: <span className="font-medium">{data.username}</span></span>
              </div>

              {/* Actions */}
              <div className="mt-4 flex gap-2">
                {onSwap && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSwap(request);
                    }}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                    title="Swap source and target spots"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Swap
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReview(request);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Review Request
                </button>
              </div>
            </div>
          </Popup>
        </Marker>
      </React.Fragment>
    );
  };

  const renderAddMarker = (request: SpotRequest) => {
    const data = request.data as SpotAddRequest;

    // Skip if coordinates are invalid
    if (!isValidCoordinate(data.latitude) || !isValidCoordinate(data.longitude)) {
      console.warn(`Add request ${request.id} has invalid coordinates, skipping map marker`);
      return null;
    }

    const isSelected = isRequestSelected(request);
    const isHovered = isRequestHovered(request);

    return (
      <Marker
        key={`add-${request.id}`}
        position={[data.latitude, data.longitude]}
        icon={createAddMarkerIcon(isSelected, isHovered)}
        eventHandlers={{
          click: () => onRequestSelect(request),
        }}
      >
        <Popup className="add-popup" maxWidth={350}>
          <div className="p-3">
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="font-bold text-gray-900">ADD SPOT REQUEST</span>
            </div>

            {/* Spot name */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
              <div className="text-xs font-medium text-green-600 mb-1">Proposed Name</div>
              <div className="font-semibold text-lg text-gray-900">"{data.spotname}"</div>
            </div>

            {/* Location */}
            <div className="text-sm text-gray-500 mb-3">
              <div className="flex items-center gap-2">
                {data.alpha2 && (
                  <img
                    src={`https://flagcdn.com/w20/${data.alpha2.toLowerCase()}.png`}
                    alt={data.alpha2}
                    className="w-5 h-auto"
                  />
                )}
                <span>{data.countryName || "Unknown location"}</span>
              </div>
              <div className="mt-1 text-xs text-gray-400">
                {data.latitude.toFixed(6)}, {data.longitude.toFixed(6)}
              </div>
            </div>

            {/* User */}
            {data.username && (
              <div className="text-sm text-gray-500 mb-3">
                Requested by: <span className="font-medium">{data.username}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReview(request);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Review Request
              </button>
            </div>
          </div>
        </Popup>
      </Marker>
    );
  };

  const renderRenameMarker = (request: SpotRequest) => {
    const data = request.data as SpotAddRequest;

    // Skip if coordinates are invalid
    if (!isValidCoordinate(data.latitude) || !isValidCoordinate(data.longitude)) {
      console.warn(`Rename request ${request.id} has invalid coordinates, skipping map marker`);
      return null;
    }

    const isSelected = isRequestSelected(request);
    const isHovered = isRequestHovered(request);

    return (
      <Marker
        key={`rename-${request.id}`}
        position={[data.latitude, data.longitude]}
        icon={createRenameMarkerIcon(isSelected, isHovered)}
        eventHandlers={{
          click: () => onRequestSelect(request),
        }}
      >
        <Popup className="rename-popup" maxWidth={350}>
          <div className="p-3">
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <span className="font-bold text-gray-900">RENAME REQUEST</span>
            </div>

            {/* Name change */}
            <div className="space-y-2 mb-3">
              <div className="bg-gray-100 rounded-lg p-2">
                <div className="text-xs font-medium text-gray-500 mb-1">Current Name</div>
                <div className="font-medium text-gray-600 line-through">"{data.oldname}"</div>
              </div>
              <div className="flex justify-center">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                <div className="text-xs font-medium text-blue-600 mb-1">Proposed Name</div>
                <div className="font-semibold text-blue-700">"{data.newname}"</div>
              </div>
            </div>

            {/* User */}
            {data.username && (
              <div className="text-sm text-gray-500 mb-3">
                Requested by: <span className="font-medium">{data.username}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReview(request);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Review Request
              </button>
            </div>
          </div>
        </Popup>
      </Marker>
    );
  };

  return (
    <>
      {requests.map((request) => {
        switch (request.type) {
          case "merge":
            return renderMergeMarkers(request);
          case "add":
            return renderAddMarker(request);
          case "rename":
            return renderRenameMarker(request);
          default:
            return null;
        }
      })}
    </>
  );
};

export default RequestMapMarkers;
